import PipelineNodeFieldMap from '@components/pipelineNodes/mapping/PipelineNodeFieldMap.component';
import PipelineNodeJoinTreeLabel from '@components/pipelineNodes/mapping/PipelineNodeJoinTreeLabel.component';
import PipelineNodeIcon, { DashboardIcon } from '@components/pipelineNodes/PipelineNodeIcon.component';
import PipelineNodeName from '@components/pipelineNodes/PipelineNodeName.component';
import { ReadOnly } from '@components/project/DraftModeRequired.component';
import InfoAlert from '@components/statusIndicators/InfoAlert.component';
import { Analysis } from '@models/analysis';
import { Dashboard } from '@models/dashboard';
import { PublishPlan } from '@models/draftVersion';
import { Pipeline } from '@models/pipeline';
import { PipelineNode, PipelineNodeField, PipelineNodeMeasureJoinTree, PipelineNodeRelationship } from '@models/pipelineNode';
import { ReportBuilderDimension, ReportBuilderDimensionJoinTree, ReportBuilderMeasure } from '@models/reportBuilder';
import { SourceRecordType } from '@models/source';
import { Visualization } from '@models/visualization';
import { Pane, PaneContent } from '@pages/PageStructure.component';
import { getGroupValueForNodeType, getNodeTypeConfigFromNodeTypeValue } from '@services/modeling.service';
import { Component, ReactNode, useCallback, useMemo, useState } from 'react';
import { Badge, Form } from 'react-bootstrap';
import { CopyBlock, atomOneLight } from 'react-code-blocks';
import ConfettiExplosion from 'react-confetti-explosion';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

const NodeFieldDisplay = (props: {field: PipelineNodeField, showMapOptions?: boolean, showMergeBehavior?: boolean}) => {
    return <div className="border p-1 rounded d-flex flex-column h-100">
        <h5 className="mb-0">{props.field.label} <Badge bg="secondary">{props.field.type}</Badge></h5>
        <p className="text-muted font-12 mb-0">{props.field.description || 'No description'}</p>
        {props.field.custom_sql && <div className="mt-2">
            <h6 className="mb-0">Formula</h6>
            <code>{props.field.custom_sql}</code>
        </div>}
        {props.field.transformer && <div className="mt-2">
            <h6 className="mb-0">Transformer</h6>
            <code>{props.field.transformer}</code>
        </div>}
        {props.showMergeBehavior && props.field.merge_behavior && <div className="mt-2">
            <h6 className="mb-0">Merge Behavior</h6>
            <code>{props.field.merge_behavior}</code>
        </div>}
        {props.showMapOptions && <div className="flex-1 mt-2">
            <h5 className="mb-0">Source Columns</h5>
            <PipelineNodeFieldMap
                mapOptions={props.field.map_options}
                onChangeMapOptions={() => {}}
                showPriority={false}
                isUnlocked={true}
                compact={true}
            />
        </div>}

    </div>
}

interface NodeFieldDiffProps {
    newValue: PipelineNodeField;
    originalValue: PipelineNodeField;
    diff: any[];
    nodeType: string;
}


const TwoColumnGrid = ({ children, className }: { children: ReactNode[], className?: string }) => {
    return (
        <div
            className={className}
            style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '2rem',
                
            }}
        >
            {children}
        </div>
    );
};



const NodeFieldDiff = (props: NodeFieldDiffProps) => {
    const showMapOptions = props.diff.some(d => d.attribute === 'map_options');
    return <TwoColumnGrid>
        <div className="d-flex flex-column h-100">
            <h5 className="mb-0">Current</h5>
            <div className="flex-1">
                <NodeFieldDisplay field={props.originalValue} showMapOptions={showMapOptions} showMergeBehavior={props.nodeType == 'SUMMARIZE'}/>
            </div>
        </div>
        <div className="d-flex flex-column h-100">
            <h5 className="mb-0">Update</h5>
            <div className="flex-1">
                <NodeFieldDisplay field={props.newValue} showMapOptions={showMapOptions} showMergeBehavior={props.nodeType == 'SUMMARIZE'}/>
            </div>
        </div>
    </TwoColumnGrid>
    
}

interface JoinTreeDiffProps {
    newValue: ReportBuilderDimensionJoinTree;
    originalValue: ReportBuilderDimensionJoinTree;
}

const JoinTreeDiff = (props: JoinTreeDiffProps) => {
    console.log('props', props);
    // You can add more logic here if you want to highlight specific fields in the join tree diff
    return <TwoColumnGrid>
        <div className="d-flex flex-column h-100">
            <h5 className="mb-0">Current</h5>
            <div className="flex-1">
                {props.originalValue.ignore && <Badge bg="secondary">Disabled</Badge>}
                {!props.originalValue.ignore && props.originalValue.join_tree && <PipelineNodeJoinTreeLabel joinTree={props.originalValue.join_tree as PipelineNodeMeasureJoinTree} />}
                
                
            </div>
        </div>
        <div className="d-flex flex-column h-100">
        <h5 className="mb-0">Update</h5>
            <div className="flex-1">
                {props.newValue.ignore && <Badge bg="secondary">Disabled</Badge>}
                {!props.newValue.ignore && props.newValue.join_tree && <PipelineNodeJoinTreeLabel joinTree={props.newValue.join_tree as PipelineNodeMeasureJoinTree} />}
            </div>
        </div>
    </TwoColumnGrid>
}


interface MeasureDiffProps {
    changes: {
        attribute: string;
        change_type: string;
        new_value?: any;
        old_value?: any;
        changes?: any[];
    }[];
}

const MeasureDiff = (props: MeasureDiffProps) => {
    return <div>
        {props.changes.map((c) => {
            if (c.attribute == 'dimension_join_trees') {
                console.log('c', c);
                return <div key={`dimension-join-trees-${c.change_type}`} className="mt-2">
                    <h3 className="">{c.change_type.charAt(0).toUpperCase() + c.change_type.slice(1)} Dimension Connections</h3>
                    {c.change_type == 'updated' && c.changes?.map((f, idx) => {
                        const newValue = f.new_value;
                        const originalValue = f.original_value;
                        return <div className="mb-2">
                            <h4>Connection to <PipelineNodeName pipelineNodeId={newValue.pipeline_node_id} /></h4>
                            <JoinTreeDiff key={idx} newValue={newValue} originalValue={originalValue}  />
                        </div>
                    })}
                    {c.change_type == 'created' && <TwoColumnGrid>
                        {c.new_value.map((f: PipelineNodeField, idx: number) => {
                            return <NodeFieldDisplay key={idx} field={f} />
                        })}
                    </TwoColumnGrid>}
                   
                    {c.change_type == 'deleted' && <TwoColumnGrid>
                        {c.old_value.map((f: PipelineNodeField, idx: number) => {
                            return <NodeFieldDisplay key={idx} field={f} />
                        })}
                    </TwoColumnGrid>}
                </div>
            } else {
                return <div>
                    <h3 className="mb-0">Changed {c.attribute}</h3>
                    <p>
                        {(typeof c.old_value === 'object' && c.old_value !== null)
                            ? JSON.stringify(c.old_value)
                            : c.old_value?.toString?.() ?? String(c.old_value)
                        }
                        &rarr;
                        {(typeof c.new_value === 'object' && c.new_value !== null)
                            ? JSON.stringify(c.new_value)
                            : c.new_value?.toString?.() ?? String(c.new_value)
                        }
                    </p>
                </div>;
            }
        })}
    </div>
}

interface PipelineNodeDiffProps {
    changes: {
        attribute: string;
        change_type: string;
        new_value?: any;
        old_value?: any;
        changes?: Record<string, any>;
    }[];
    nodeType: string;
}


const PipelineNodeDiff = (props: PipelineNodeDiffProps) => {
    return <div>
        {props.changes.map((c) => {
            if (c.attribute == 'fields') {
                return <div key={`fields-${c.change_type}`} className="mt-2">
                    <h3 className="">{c.change_type.charAt(0).toUpperCase() + c.change_type.slice(1)} Columns</h3>
                    {c.change_type == 'updated' && Object.keys(c.changes || {}).map((fId: string, idx: number) => {
                        const f = c.changes![fId];
                        return <div className="mb-2"><NodeFieldDiff nodeType={props.nodeType} key={idx} newValue={f.new} originalValue={f.original} diff={f.diff} /></div>
                    })}
                    {c.change_type == 'created' && <TwoColumnGrid>
                        {c.new_value.map((f: PipelineNodeField, idx: number) => {
                            return <NodeFieldDisplay key={idx} field={f} />
                        })}
                    </TwoColumnGrid>}
                   
                    {c.change_type == 'deleted' && <TwoColumnGrid>
                        {c.old_value.map((f: PipelineNodeField, idx: number) => {
                            return <NodeFieldDisplay key={idx} field={f} />
                        })}
                    </TwoColumnGrid>}
                </div>
            } else {
                return <div>
                    <h3 className="mb-0">Changed {c.attribute}</h3>
                    
                    <p>
                        {(typeof c.old_value === 'object' && c.old_value !== null)
                            ? JSON.stringify(c.old_value)
                            : c.old_value?.toString?.() ?? String(c.old_value)
                        }
                        &rarr;
                        {(typeof c.new_value === 'object' && c.new_value !== null)
                            ? JSON.stringify(c.new_value)
                            : c.new_value?.toString?.() ?? String(c.new_value)
                        }
                    </p>
                </div>;
            }
        })}
    </div>
}


interface Props {
    loading?: boolean;
    disabled?: boolean;
    plan?: PublishPlan;
}

const Container = styled.div`

.model-changes {
    padding: 5px 5px;
}

.diff-table{
    height: 50vh;
}

.record-item {
    margin-bottom: 5px;
    padding: 3px;
    border-radius: 5px;
}

color: var(--ct-body-color);
`

const PublishPlanChanges = (props: Props) => {
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<any>(undefined);
    const [status, setStatus] = useState('');

    const [focusRecord, setFocusRecord] = useState<any>(undefined);
    const [focusDiff, setFocusDiff] = useState<any>(undefined);

    const hideDiff = useCallback(() => {
        setFocusRecord(undefined);
        setFocusDiff(undefined);
    }, []);

    const showDiff = useCallback((node: any, diff: string) => {
        setFocusRecord(node);
        setFocusDiff(diff);
    }, []);



    if (!props.plan?.has_any_changes) {
        return <InfoAlert>It looks like there are no changes to publish.</InfoAlert>
    }

    const renderDimension = (n: ReportBuilderDimension, bg_style: string, showDiff?: any,) => {
        return <div className="shadow-box p-2 mb-2">
            <div className="d-flex center-vertically">
                <DashboardIcon bgColor='purple' icon={"mdi mdi-tag"} compact={true} />
                <h2 className="flex-1 ms-1 mb-0">{n.name} <Badge bg="info">Dimension</Badge></h2>
                {showDiff && <button className="btn btn-sm btn-light" onClick={showDiff}>View JSON Diff</button>}
            </div>

        </div>
    }

    const renderMeasure = (n: ReportBuilderMeasure, bg_style: string, showDiff?: any, diff?: any) => {
        return <div className="shadow-box p-2 mb-2">
            <div className="d-flex center-vertically mb-4 pb-2 border-bottom">
                <DashboardIcon bgColor='green' icon={"mdi mdi-pound-box"} compact={true} />
                <h2 className="flex-1 ms-1 mb-0">{n.name} <Badge bg="info">Metric</Badge></h2>
                {showDiff && <button className="btn btn-sm btn-light" onClick={showDiff}>View JSON Diff</button>}
            </div>
            {showDiff && diff && <>
                <MeasureDiff changes={diff} />
            </>}
        </div>
    }
    
    const renderPipelineNode =  (n: PipelineNode, bg_style: string, showDiff?: any, diff?: any) => {
        const NodeType = () => {
            const conf = getNodeTypeConfigFromNodeTypeValue(n.node_type);
            return <>{conf.nodeType.label}</>;
        }

        return <div className="shadow-box p-2 mb-2">
            <div className="d-flex center-vertically mb-4 pb-2 border-bottom">
                <PipelineNodeIcon node={n} compact={true} />
                <h2 className="flex-1 ms-1 mb-0">{n.label} <Badge bg="info">Node</Badge></h2>
                {showDiff && <button className="btn btn-sm btn-light" onClick={showDiff}>View JSON Diff</button>}
                

            </div>
            {showDiff && diff && <>
                <PipelineNodeDiff changes={diff} nodeType={n.node_type} />
                {/* <a role="button force-link" onClick={showDiff}>Changes to {changedFieldNames.join(', ')}</a> */}
            </>}

            
        </div>
        
    } 

    const renderPipelineNodeRelationship =  (n: PipelineNodeRelationship, bg_style: string, showDiff?: any) => {
        return <div className="shadow-box p-2 mb-2">
            <div className="d-flex center-vertically">
                <DashboardIcon bgColor={'purple'} icon={"mdi mdi-transit-connection-variant"} compact={true} />
                <h2 className="flex-1 ms-1 mb-0">{n.name} <Badge bg="info">Relationship</Badge></h2>
                {showDiff && <button className="btn btn-sm btn-light" onClick={showDiff}>View JSON Diff</button>}
            </div>
        </div>
       
    } 

    const renderVisualization = (n: Visualization, bg_style: string, showDiff?: any, diff?: any) => {
        // You can add a VisualizationType if needed, similar to NodeType in renderPipelineNode
        // For now, just display the name and diff if present
        return <div className="shadow-box p-2 mb-2">
            <div className="d-flex center-vertically ">
                <DashboardIcon bgColor={'pink'} icon={"mdi mdi-chart-bar"} compact={true} />
                <h2 className="flex-1 ms-1 mb-0">{n.name} <Badge bg="info">Visualization</Badge></h2>
                {showDiff && <button className="btn btn-sm btn-light" onClick={showDiff}>View JSON Diff</button>}
            </div>
            {showDiff && diff && (
                // If you have a VisualizationDiff component, use it here. Otherwise, show nothing or a placeholder.
                // <VisualizationDiff changes={diff} />
                <></>
            )}
        </div>
    }

    const renderDashboard = (n: Dashboard, bg_style: string, showDiff?: any, diff?: any) => {
        // You can add a DashboardType if needed, similar to NodeType in renderPipelineNode
        // For now, just display the name and diff if present
        return <div className="shadow-box p-2 mb-2">
            <div className="d-flex center-vertically">
                <DashboardIcon bgColor={'primary'} icon={"mdi mdi-dashboard"} compact={true} />
                <h2 className="flex-1 ms-1 mb-0">{n.name} <Badge bg="info">Dashboard</Badge></h2>
                {showDiff && <button className="btn btn-sm btn-light" onClick={showDiff}>View JSON Diff</button>}
            </div>
            {showDiff && diff && (
                // If you have a DashboardDiff component, use it here. Otherwise, show nothing or a placeholder.
                // <DashboardDiff changes={diff} />
                <></>
            )}
        </div>
    }

 

    const renderAnalysis =  (n: Analysis, bg_style: string, showDiff?: any) => {
        return <div className="shadow-box p-2 mb-2">
            <div className="d-flex center-vertically">
                <DashboardIcon bgColor={'pink'} icon={"mdi mdi-view-dashboard"} compact={true} />
                <h2 className="flex-1 ms-1 mb-0">{n.name} <Badge bg="info">Chart</Badge></h2>
                {showDiff && <button className="btn btn-sm btn-light" onClick={showDiff}>View JSON Diff</button>}
            </div>
            {/* If you have an AnalysisDiff component, you can render it here if needed, similar to DashboardDiff */}
        </div>
    } 
    

    return (
        <Container>
        
        <div className='diff-table mb-4'>
                {focusDiff && <div className='row'>
                    <div className='col-12'>
                            <h2>
                                {focusRecord?.name || 'Unnamed Record'} - Diff <a role="button" className="float-right" onClick={() => hideDiff()}>X</a>
                            </h2>
                            
                    </div>
                    <div className='col-12'>
                            <CopyBlock
                                text={JSON.stringify(focusDiff, null, 2) as string}
                                language="json"
                                showLineNumbers={false}
                                theme={atomOneLight}
                                codeBlock={true}
                            />
                    </div>
                </div>}

                {!focusDiff && <>
                <h1>New Objects</h1>
                {props.plan.diff.PipelineNode.created.length === 0 &&
                 props.plan.diff.PipelineNodeRelationship.created.length === 0 &&
                 props.plan.diff.Visualization.created.length === 0 &&
                 props.plan.diff.Dashboard.created.length === 0 &&
                 props.plan.diff.Dimension.created.length === 0 &&
                 props.plan.diff.Measure.created.length === 0 &&
                 props.plan.diff.Analysis.created.length === 0 && (
                    <div className="text-muted">None</div>
                )}
                {props.plan.diff.PipelineNode.created.map((r, idx) => renderPipelineNode(r, 'alert-warning'))}
                {props.plan.diff.PipelineNodeRelationship.created.map((r) => renderPipelineNodeRelationship(r, 'alert-warning'))}
                {props.plan.diff.Visualization.created.map((r) => renderVisualization(r, 'alert-warning'))}
                {props.plan.diff.Dashboard.created.map((r) => renderDashboard(r, 'alert-warning'))}
                {props.plan.diff.Dimension.created.map((r) => renderDimension(r, 'alert-warning'))}
                {props.plan.diff.Measure.created.map((r) => renderMeasure(r, 'alert-warning'))}
                {props.plan.diff.Analysis.created.map((r) => renderAnalysis(r, 'alert-warning'))}
                <hr />
                <h1>Updated Objects</h1>
                {props.plan.diff.PipelineNode.updated.length === 0 &&
                 props.plan.diff.PipelineNodeRelationship.updated.length === 0 &&
                 props.plan.diff.Visualization.updated.length === 0 &&
                 props.plan.diff.Dashboard.updated.length === 0 &&
                 props.plan.diff.Dimension.updated.length === 0 &&
                 props.plan.diff.Measure.updated.length === 0 &&
                 props.plan.diff.Analysis.updated.length === 0 && (
                    <div className="text-muted">None</div>
                )}
                {props.plan.diff.PipelineNode.updated.map((r, idx) => renderPipelineNode(r, 'alert-info', () => showDiff(r, props.plan!.diff.PipelineNode.updated_diff[idx]), props.plan!.diff.PipelineNode.updated_diff[idx]))}
                {props.plan.diff.PipelineNodeRelationship.updated.map((r, idx) => renderPipelineNodeRelationship(r, 'alert-info', () => showDiff(r, props.plan!.diff.PipelineNodeRelationship.updated_diff[idx])))}
                {props.plan.diff.Visualization.updated.map((r, idx) => renderVisualization(r, 'alert-info', () => showDiff(r, props.plan!.diff.Visualization.updated_diff[idx])))}
                {props.plan.diff.Dashboard.updated.map((r, idx) => renderDashboard(r, 'alert-info', () => showDiff(r, props.plan!.diff.Dashboard.updated_diff[idx])))}
                {props.plan.diff.Dimension.updated.map((r, idx) => renderDimension(r, 'alert-info', () => showDiff(r, props.plan!.diff.Dimension.updated_diff[idx])))}
                {props.plan.diff.Measure.updated.map((r, idx) => renderMeasure(r, 'alert-info', () => showDiff(r, props.plan!.diff.Measure.updated_diff[idx]), props.plan!.diff.Measure.updated_diff[idx]))}
                {props.plan.diff.Analysis.updated.map((r, idx) => renderAnalysis(r, 'alert-info', () => showDiff(r, props.plan!.diff.Analysis.updated_diff[idx])))}
                <hr />

                <h1>Deleted Objects</h1>
                {props.plan.diff.PipelineNode.deleted.length === 0 &&
                 props.plan.diff.PipelineNodeRelationship.deleted.length === 0 &&
                 props.plan.diff.Visualization.deleted.length === 0 &&
                 props.plan.diff.Dashboard.deleted.length === 0 &&
                 props.plan.diff.Dimension.deleted.length === 0 &&
                 props.plan.diff.Measure.deleted.length === 0 &&
                 props.plan.diff.Analysis.deleted.length === 0 && (
                    <div className="text-muted">None</div>
                )}
                {props.plan.diff.PipelineNode.deleted.map((r) => renderPipelineNode(r, 'alert-danger'))}
                {props.plan.diff.PipelineNodeRelationship.deleted.map((r) => renderPipelineNodeRelationship(r, 'alert-danger'))}
                {props.plan.diff.Visualization.deleted.map((r) => renderVisualization(r, 'alert-danger'))}
                {props.plan.diff.Dashboard.deleted.map((r) => renderDashboard(r, 'alert-danger'))}
                {props.plan.diff.Dimension.deleted.map((r) => renderDimension(r, 'alert-danger'))}
                {props.plan.diff.Measure.deleted.map((r) => renderMeasure(r, 'alert-danger'))}
                {props.plan.diff.Analysis.deleted.map((r) => renderAnalysis(r, 'alert-danger'))}
                <div className="pb-5"></div>
                </>
                }
        </div>
        </Container>
    )
}

export default PublishPlanChanges;

